import { faExternalLinkAlt, faEye, faEyeSlash, faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect, useContext } from 'react';
import { Button, FormFeedback, FormGroup, Input, InputGroup, Label, UncontrolledTooltip } from 'reactstrap';
import { useAuth0 } from '../../../../../../auth0/reactAuth0Spa';
import {
  checkIfZoomURLAndIfHasPassword,
  checkLoginForError,
  formatBearerToken,
  formatMeetingUrlForDisplay,
  isRezrateEmbeddedMeetingUrl,
  isValidURL,
  REZRATE_EMBEDDED_MEETING_PLACEHOLDER,
  swalErrorMessage,
  urlWithHttpProtocol,
} from '../../../../../../Common.functions';
import { putData } from '../../../../../../Services/dataApi';
import { DepartmentContext } from '../../../../../../DepartmentWrapper';
import { toast } from 'react-toastify';
import style from './style';

const SettingsDropDown = ({ dateData, onUpdate, setParentChanges }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [MainRoomURL, setMainRoomURL] = useState('');
  const [MainRoomURLDetails, setMainRoomURLDetails] = useState('');
  const [hasChange, setHasChange] = useState(false);
  const [meetingType, setMeetingType] = useState('external'); // external | embedded

  const { getTokenSilently, loginWithRedirect } = useAuth0();

  const dContext = useContext(DepartmentContext);

  const embeddedUiEnabled =
    dContext &&
    dContext.department &&
    dContext.department.VirtualMeetingMode === 'Internal' &&
    Boolean(dContext.department.enableEmbeddedMeetings);

  const zoomDetails = checkIfZoomURLAndIfHasPassword(MainRoomURL);
  const { isZoomURL, hasPassword } = zoomDetails;

  useEffect(() => {
    if (dateData) {
      const initialUrl = dateData.MainRoomURL || '';
      const isEmbedded = isRezrateEmbeddedMeetingUrl(initialUrl);

      setMainRoomURL(embeddedUiEnabled ? initialUrl : isEmbedded ? '' : initialUrl);
      setMainRoomURLDetails(dateData.MainRoomURLDetails || '');
      setMeetingType(embeddedUiEnabled && isEmbedded ? 'embedded' : 'external');
    }
  }, [JSON.stringify(dateData), embeddedUiEnabled]);

  useEffect(() => {
    checkChanges();
  }, [MainRoomURL, MainRoomURLDetails]);

  useEffect(() => {
    setParentChanges(hasChange);
  }, [hasChange]);

  const updateEntry = (entry) => {
    setIsUpdating(true);
    getTokenSilently()
      .then((token) => {
        putData(
          'department/interviewDate/putEntry',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Season: dContext.season.pk_Season,
            pk_InterviewDate: dateData.pk_InterviewDate,
          },
          { entry },
          formatBearerToken(token),
        )
          .then((result) => {
            setIsUpdating(false);
            if (result.data.error) {
              swalErrorMessage();
              return;
            }
            toast.success('Link settings Saved!');
            if (onUpdate) {
              onUpdate();
            }
          })
          .catch((err) => {
            setIsUpdating(false);
            swalErrorMessage();
          });
      })
      .catch((err) => {
        swalErrorMessage();
        checkLoginForError({ err, loginWithRedirect });
        return false;
      });
  };

  const checkChanges = () => {
    const oldURL = dateData.MainRoomURL || '';
    const oldDetails = dateData.MainRoomURLDetails || '';
    let newHasChange = false;

    if (MainRoomURL !== oldURL) {
      newHasChange = true;
    }

    if (MainRoomURLDetails !== oldDetails) {
      newHasChange = true;
    }

    setHasChange(newHasChange);
  };

  const saveFields = () => {
    let mainRoomUrl = '';
    let mainRoomUrlDetails = '';

    if (embeddedUiEnabled && meetingType === 'embedded') {
      mainRoomUrl = REZRATE_EMBEDDED_MEETING_PLACEHOLDER;
    } else if (MainRoomURL.trim() !== '') {
      if (isRezrateEmbeddedMeetingUrl(MainRoomURL)) {
        // Embedded placeholder is not valid when embedded meetings are disabled (or not selected).
        mainRoomUrl = '';
      } else {
        mainRoomUrl = urlWithHttpProtocol(MainRoomURL);
      }
    }

    if (MainRoomURLDetails.trim() !== '') {
      mainRoomUrlDetails = MainRoomURLDetails;
    }
    const entry = {
      MainRoomURL: mainRoomUrl,
      MainRoomURLDetails: mainRoomUrlDetails,
    };
    setHasChange(false);
    updateEntry(entry);
  };

  return (
    <div>
      <div>
        <FormGroup>
          <Label for="meetingLink">All Day Meeting Link (Used as break room or conference room)</Label>
          {embeddedUiEnabled ? (
            <div style={{ marginBottom: 10 }}>
              <Label style={{ fontWeight: 'bold' }}>Meeting Type</Label>
              <div style={{ display: 'flex', gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
                <Label style={{ cursor: 'pointer', margin: 0 }}>
                  <Input
                    type="radio"
                    name="allDayMeetingType"
                    checked={meetingType === 'external'}
                    onChange={() => {
                      setMeetingType('external');
                      if (isRezrateEmbeddedMeetingUrl(MainRoomURL)) {
                        setMainRoomURL('');
                      }
                    }}
                    style={{ marginRight: 8 }}
                  />
                  External URL
                </Label>
                <Label style={{ cursor: 'pointer', margin: 0 }}>
                  <Input
                    type="radio"
                    name="allDayMeetingType"
                    checked={meetingType === 'embedded'}
                    onChange={() => {
                      setMeetingType('embedded');
                      setMainRoomURL(REZRATE_EMBEDDED_MEETING_PLACEHOLDER);
                      if (!MainRoomURLDetails || `${MainRoomURLDetails}`.trim() === '') {
                        setMainRoomURLDetails(
                          'RezRATE will generate embedded meetings automatically as needed. Use “Join All Day Meeting” to enter.',
                        );
                      }
                    }}
                    style={{ marginRight: 8 }}
                  />
                  RezRATE Embedded Meetings
                </Label>
              </div>
            </div>
          ) : null}
          <InputGroup>
            <Input
              style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
              invalid={
                (!embeddedUiEnabled || meetingType !== 'embedded') &&
                ((MainRoomURL && !isValidURL(MainRoomURL)) || (isZoomURL && !hasPassword))
              }
              id="meetingLink"
              type="text"
              placeholder={
                embeddedUiEnabled && meetingType === 'embedded'
                  ? 'RezRATE Embedded Meetings (auto-generated)'
                  : 'Enter URL here. . .'
              }
              value={embeddedUiEnabled && meetingType === 'embedded' ? formatMeetingUrlForDisplay(MainRoomURL) : MainRoomURL}
              disabled={embeddedUiEnabled && meetingType === 'embedded'}
              onChange={(e) => {
                setMainRoomURL(e.target.value);
              }}
            />
            <FormFeedback invalid={(!embeddedUiEnabled || meetingType !== 'embedded') && (!MainRoomURL || !isValidURL(MainRoomURL))}>
              {!isValidURL(MainRoomURL) ? 'Unusual URL found. Unusual URL detected. Please double check.' : ''}
            </FormFeedback>
            <FormFeedback
              invalid={
                (!embeddedUiEnabled || meetingType !== 'embedded') &&
                ((MainRoomURL && !isValidURL(MainRoomURL)) || (isZoomURL && !hasPassword))
              }
            >
              {isZoomURL && !hasPassword
                ? 'Zoom URL detected, but without a password. We recommend utilizing embedded passwords for Zoom meetings.'
                : ''}{' '}
              {isZoomURL && !hasPassword ? (
                <a href="https://www.rezrate.com/support/zoom-password/" target="_blank" rel="noopener noreferrer">
                  {' '}
                  Get Zoom Support
                </a>
              ) : (
                ''
              )}
            </FormFeedback>
          </InputGroup>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 10,
            }}
          >
            <Button
              id={`testButton_${dateData.pk_InterviewDate}`}
              disabled={!MainRoomURL || meetingType === 'embedded'}
              style={style.testButton}
              onClick={() => {
                const urlToOpen = urlWithHttpProtocol(MainRoomURL);
                window.open(urlToOpen);
              }}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginRight: 10 }} /> Preview
            </Button>
            {meetingType === 'embedded' ? (
              <div style={{ marginLeft: 10, fontSize: 12, color: '#666' }}>
                All-day embedded meetings are generated on demand.
              </div>
            ) : null}
          </div>
        </FormGroup>
      </div>

      <div>
        <Label for="helpText">All Day Meeting Help Text (This will appear on the join meeting card)</Label>
        <Input
          id="helpText"
          type="textarea"
          rows={5}
          placeholder="Optional description could include information such as password or dial in number"
          value={MainRoomURLDetails}
          onChange={(e) => {
            setMainRoomURLDetails(e.target.value);
          }}
        />
      </div>

      <div
        style={{ ...style.simpleRow, justifyContent: 'center', alignItems: 'center', marginTop: 20 }}
        onClick={() => {
          saveFields();
        }}
      >
        <Button disabled={!hasChange} style={{ width: 200 }} color="success">
          Save
        </Button>
      </div>
    </div>
  );
};

export default SettingsDropDown;
